/**
 * Voice moderation events, with inline playback.
 *
 * The audio element points at `/api/voice/:id/clip`, which re-checks authorisation and writes an
 * audit row on every request. That is why there is no direct link to the clip file and no
 * preloading: `preload="none"` means the request — and therefore the audit row — happens when
 * somebody actually presses play, not when the table renders.
 *
 * New events arrive over the shared stream and are prepended, so a flagged utterance shows up while
 * the tab is open.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { ShareDialog } from "./ShareDialog";
import { useQuery } from "@tanstack/react-query";
import { fetchVoiceEvents, fetchVoiceRules } from "../api";
import { formatBytes, formatTimestamp } from "../format";
import type { VoiceEvent } from "../types";

interface Props {
  liveEvents: VoiceEvent[];
  onOpenPlayer: (player: string) => void;
  /** From `?event=<id>` — the link a Discord alert carries. Empty when browsing normally. */
  focusEventId: string;
  onClearFocus: () => void;
  /** Needed to mint a share link, which is a state-changing POST. */
  csrfToken: string;
}

export function VoiceTab({
  liveEvents,
  onOpenPlayer,
  focusEventId,
  onClearFocus, csrfToken }: Props): JSX.Element {
  const [sharing, setSharing] = useState<VoiceEvent | null>(null);
  const [rule, setRule] = useState("");
  const [punished, setPunished] = useState("");
  const [player, setPlayer] = useState("");
  const focusedRow = useRef<HTMLTableRowElement | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    // A focused event replaces the other filters rather than combining with them. Somebody following
    // a link from Discord wants that specific utterance, and intersecting it with whatever filters
    // the tab was last left on is exactly how such a link ends up showing an empty table.
    if (focusEventId) {
      params.set("id", focusEventId);
      return params.toString();
    }
    if (rule) params.set("matched_rule", rule);
    if (punished) params.set("punished", punished);
    if (player) params.set("player", player);
    return params.toString();
  }, [rule, punished, player, focusEventId]);

  const rules = useQuery({ queryKey: ["voice-rules"], queryFn: fetchVoiceRules });
  const events = useQuery({
    queryKey: ["voice", query],
    queryFn: () => fetchVoiceEvents(query, null),
  });

  // Live arrivals sit on top of the fetched page, de-duplicated by id in case a refetch has already
  // picked one up.
  const [live, setLive] = useState<VoiceEvent[]>([]);
  useEffect(() => {
    setLive(liveEvents);
  }, [liveEvents]);

  const rows = useMemo(() => {
    const fetched = events.data?.events ?? [];
    // While focused on one event, live arrivals are held back. Prepending unrelated new events to a
    // table somebody opened to look at one specific utterance would push it out from under them.
    if (focusEventId) return fetched;
    const seen = new Set(fetched.map((event) => event.id));
    const matchesFilters = (event: VoiceEvent): boolean =>
      (!rule || event.matched_rule === rule) &&
      (!punished || String(Number(event.punished)) === punished) &&
      (!player || event.player.toLowerCase() === player.toLowerCase());
    return [...live.filter((e) => !seen.has(e.id) && matchesFilters(e)), ...fetched];
  }, [events.data, live, rule, punished, player, focusEventId]);

  // Scroll the linked event into view once it has actually rendered. Depends on the row data, not
  // just the id, because the element does not exist until the fetch resolves.
  useEffect(() => {
    if (!focusEventId || !focusedRow.current) return;
    focusedRow.current.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focusEventId, rows]);

  return (
    <>
      {focusEventId && (
        <div className="focus-banner">
          <span>
            Showing one event linked from Discord
            {events.isFetched && rows.length === 0
              ? " — it no longer exists, or you do not have access to it."
              : "."}
          </span>
          <button type="button" className="input" onClick={onClearFocus}>
            Show all voice events
          </button>
        </div>
      )}
      <div className="filterbar">
        <input
          className="input"
          placeholder="Player"
          value={player}
          onChange={(event) => setPlayer(event.target.value)}
          aria-label="Filter voice events by player"
          style={{ width: "16ch" }}
        />
        <select
          className="input"
          value={rule}
          onChange={(event) => setRule(event.target.value)}
          aria-label="Filter by rule"
        >
          <option value="">All rules</option>
          {(rules.data?.rules ?? []).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <select
          className="input"
          value={punished}
          onChange={(event) => setPunished(event.target.value)}
          aria-label="Filter by outcome"
        >
          <option value="">Any outcome</option>
          <option value="1">Punished</option>
          <option value="0">Logged only</option>
        </select>
      </div>

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Player</th>
              <th>Rule</th>
              <th>Outcome</th>
              <th>Transcript</th>
              <th>Clip</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  {events.isLoading ? "Loading…" : "No voice events match these filters."}
                </td>
              </tr>
            )}
            {rows.map((event) => (
              <tr
                key={event.id}
                ref={String(event.id) === focusEventId ? focusedRow : undefined}
                className={String(event.id) === focusEventId ? "row-focused" : undefined}
              >
                <td className="mono">{formatTimestamp(event.occurred_at)}</td>
                <td>
                  <button
                    type="button"
                    className="who"
                    onClick={() => onOpenPlayer(event.player)}
                  >
                    {event.player}
                  </button>
                  <div className="mono" style={{ fontSize: 10.5 }}>
                    {event.server}
                  </div>
                </td>
                <td className="mono">{event.matched_rule ?? "-"}</td>
                <td className={event.punished ? "verdict-punished" : "verdict-logged"}>
                  {event.punished ? "Punished" : "Logged only"}
                </td>
                <td className="transcript">{event.transcript ?? <em>no transcript</em>}</td>
                <td>
                  {event.has_clip ? (
                    <>
                      {/* preload="none" so the audit row is written when somebody presses play,
                          not when the table paints. */}
                      <audio controls preload="none" src={`/api/voice/${event.id}/clip`} />
                      <div className="mono" style={{ fontSize: 10.5 }}>
                        {event.duration_ms ? `${Math.round(event.duration_ms / 1000)}s` : ""}{" "}
                        {formatBytes(event.clip_bytes)}
                      </div>
                      <div className="row" style={{ gap: 6, marginTop: 4 }}>
                        {/* A plain link rather than fetch(): the browser handles the save dialog and
                            the server's Content-Disposition names the file. */}
                        <a
                          className="btn"
                          style={{ padding: "3px 9px", fontSize: 12 }}
                          href={`/api/voice/${event.id}/download`}
                          download
                        >
                          Download
                        </a>
                        <button
                          type="button"
                          className="btn"
                          style={{ padding: "3px 9px", fontSize: 12 }}
                          onClick={() => setSharing(event)}
                        >
                          Share link
                        </button>
                      </div>
                    </>
                  ) : (
                    <span className="mono" style={{ fontSize: 11 }}>
                      no clip
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sharing && (
        <ShareDialog
          eventId={sharing.id}
          player={sharing.player}
          csrfToken={csrfToken}
          onClose={() => setSharing(null)}
        />
      )}
    </>
  );
}
